import api from '../../../../common/utils/api';
import Common from '../../../../common/services/Common';
import type { EligibilityException, LoanExceptionUpsertRequest } from './types';
import { mapLoanExceptionResponse } from './utils/helpers';

export interface SaveEligibilityExceptionArgs {
  loanId: number;
  programId: number;
  body: LoanExceptionUpsertRequest;
}

export interface GetEligibilityExceptionArgs {
  loanId: number;
  programId: number;
}

export interface DeleteEligibilityExceptionArgs {
  loanId: number;
  programId: number;
}

// Persists the eligibility exception for one loan + program via the partner-service proxy.
export const saveEligibilityException = async ({
  loanId,
  programId,
  body,
}: SaveEligibilityExceptionArgs) => {
  const originId = await Common.getOriginId();
  return api.upsertEligibilityException({
    loanId,
    programId,
    originId,
    body: JSON.stringify(body),
    headers: { 'X-Elli-PAT': await Common.getPATToken() },
  });
};

// Fetches the saved exception for one loan + program; returns null when none exists or the request fails.
export const getEligibilityException = async ({
  loanId,
  programId,
}: GetEligibilityExceptionArgs): Promise<EligibilityException | null> => {
  const originId = await Common.getOriginId();
  try {
    const raw = await api.getEligibilityException({
      loanId,
      programId,
      originId,
      headers: { 'X-Elli-PAT': await Common.getPATToken() },
    });
    return mapLoanExceptionResponse(raw);
  } catch {
    return null;
  }
};

// Deletes the whole exception. Resolves on 204 No Content; rejects on failure.
export const deleteEligibilityException = async ({
  loanId,
  programId,
}: DeleteEligibilityExceptionArgs): Promise<void> => {
  const originId = await Common.getOriginId();
  await api.deleteEligibilityException({
    loanId,
    programId,
    originId,
    headers: { 'X-Elli-PAT': await Common.getPATToken() },
  });
};
